import { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { events as srslyEvents, type SrslyAnyEventHandler } from '@sly-rentals/core';

import type { EventEntry, SchemaField } from '../../data/instructions.generated';
import { useCodeFlavor, type CodeFlavor } from '../../lib/code-flavor-context';
import { useNetwork } from '../../lib/network-context';
import { highlightedJson } from '../../lib/json-tree';
import { MinimalCodeBlock, type CodeTab } from './CodeBlock';

interface EventPageProps {
  eventEntry: EventEntry;
}

type LiveEvent = Parameters<SrslyAnyEventHandler>[0];

const MAX_LIVE = 8;

function buildSnippet(entry: EventEntry, flavor: CodeFlavor): string {
  const importPath = flavor === 'kit' ? '@sly-rentals/core' : '@sly-rentals/core/legacy';
  return [
    `import { events } from '${importPath}';`,
    '',
    `const off = events.${entry.group}.${entry.verb}.on(evt => {`,
    `  // evt is fully typed as ${entry.name}Event`,
    `  console.log(evt);`,
    `});`,
    '',
    `// stop just this handler:`,
    `off();`,
  ].join('\n');
}

function explorerHref(signature: string, rpcUrl: string, network: string | undefined): string {
  if (network === 'mainnet-beta' || network === undefined) {
    return `https://solscan.io/tx/${signature}`;
  }
  if (network === 'devnet') {
    return `https://solscan.io/tx/${signature}?cluster=devnet`;
  }
  return `https://solscan.io/tx/${signature}?cluster=custom&customUrl=${encodeURIComponent(rpcUrl)}`;
}

function trunc(s: string, head = 6, tail = 6): string {
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

export function EventPage({ eventEntry }: EventPageProps) {
  const net = useNetwork();
  const { flavor, setFlavor } = useCodeFlavor();
  const [feed, setFeed] = useState<LiveEvent[]>([]);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  // Fresh feed per event navigation.
  const lastEventName = useRef(eventEntry.name);
  useEffect(() => {
    if (lastEventName.current !== eventEntry.name) {
      lastEventName.current = eventEntry.name;
      setFeed([]);
      setExpandedIdx(null);
    }
  }, [eventEntry.name]);

  // Subscribe to just this event's channel. The dotted path is data-driven
  // off the catalog, so adding a new event upstream wires this page up too.
  useEffect(() => {
    if (!net.eventsEnabled) {
      setFeed([]);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const channel = (srslyEvents as any)[eventEntry.group]?.[eventEntry.verb] as
      | { on: (cb: (evt: LiveEvent) => void) => () => void }
      | undefined;
    if (!channel) return;
    const off = channel.on(evt => {
      setFeed(prev => [evt, ...prev].slice(0, MAX_LIVE));
    });
    return off;
  }, [eventEntry.group, eventEntry.verb, net.eventsEnabled]);

  const tabs = useMemo<CodeTab[]>(
    () => [
      { id: 'kit', label: '@solana/kit', source: buildSnippet(eventEntry, 'kit') },
      { id: 'web3js', label: '@solana/web3.js', source: buildSnippet(eventEntry, 'web3js') },
    ],
    [eventEntry]
  );

  return (
    <div className="ix-grid">
      <div>
        <div className="ix-head">
          <div className="ix-kicker">
            <span className="br">[</span>
            <span className="group">&nbsp;events</span>
            <span className="br">/</span>
            <span className="group">&nbsp;{eventEntry.group}</span>
            <span className="br">/</span>
            <span className="name">&nbsp;{eventEntry.verb}&nbsp;</span>
            <span className="br">]</span>
          </div>
          <h1 className="ix-title">{eventEntry.name}</h1>
          {eventEntry.description && (
            <p className="ix-sub" style={{ whiteSpace: 'pre-line' }}>
              {eventEntry.description}
            </p>
          )}
        </div>

        <h2 className="ix-section-label">Payload {eventEntry.name}Event</h2>
        <div className="schema">
          {eventEntry.schema.map(f => (
            <SchemaRow key={f.name} field={f} />
          ))}
          <div className="schema-row">
            <div className="schema-meta">
              <span className="schema-name">slot</span>
              <span className="schema-type">bigint</span>
            </div>
            <p className="schema-desc">
              Slot the tx landed in. Added by the listener; not in the on-chain log.
            </p>
          </div>
          <div className="schema-row">
            <div className="schema-meta">
              <span className="schema-name">signature</span>
              <span className="schema-type">string</span>
            </div>
            <p className="schema-desc">Transaction signature that emitted the event.</p>
          </div>
        </div>
      </div>

      <aside
        style={{
          position: 'sticky',
          top: 24,
          alignSelf: 'start',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <h2 className="ix-section-label">Live preview</h2>
        <MinimalCodeBlock
          tabs={tabs}
          active={flavor}
          onActiveChange={id => setFlavor(id as CodeFlavor)}
        />

        <h2 className="ix-section-label">
          Live feed{' '}
          <span style={{ color: 'var(--ink-mute)', letterSpacing: '0.1em' }}>
            {net.eventsEnabled ? `(last ${MAX_LIVE})` : '(disabled)'}
          </span>
        </h2>
        {!net.eventsEnabled ? (
          <div className="console-warn">
            Live events are off. Toggle the <span className="mono">on/off</span> pip beside the
            <span className="mono"> Events listener</span> entry in the sidebar to enable.
          </div>
        ) : feed.length === 0 ? (
          <div
            className="console-fetch"
            style={{ borderStyle: 'dashed', borderColor: 'var(--line)' }}
          >
            <span className="empty">
              No <span className="mono cyan">{eventEntry.name}</span> events yet. The feed updates
              live; submit a relevant instruction (or wait for one) to populate.
            </span>
          </div>
        ) : (
          feed.map((evt, i) => {
            const isOpen = expandedIdx === i;
            return (
              <div key={`${evt.signature}-${i}`} className={`evt-card${isOpen ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="evt-card-head"
                  onClick={() => setExpandedIdx(isOpen ? null : i)}
                  aria-expanded={isOpen}
                >
                  <span className="evt-card-sig">{trunc(evt.signature)}</span>
                  <span className="evt-card-slot">slot {String(evt.slot)}</span>
                  <span className="evt-card-toggle">{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && (
                  <>
                    <pre className="event-toast-raw">{highlightedJson(evt)}</pre>
                    <div className="evt-card-foot">
                      <a
                        href={explorerHref(evt.signature, net.rpcUrl, net.network)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        view on solscan <ExternalLink size={11} strokeWidth={1.6} />
                      </a>
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </aside>
    </div>
  );
}

function SchemaRow({ field }: { field: SchemaField }) {
  return (
    <div className="schema-row">
      <div className="schema-meta">
        <span className="schema-name">{field.name}</span>
        <span className="schema-type">{field.type}</span>
      </div>
      {field.docs && <p className="schema-desc">{field.docs}</p>}
    </div>
  );
}
